import { ColumnDef, SortingState } from '@tanstack/react-table';
import { type LucideIcon } from 'lucide-react';
import type { KeywordState, ActionDetails } from '@/types/action-types';
import { CompetitorData } from './apis';

export enum ActionType {
  DELETE_KEYWORD = 'delete_keyword',
  ADD_KEYWORD = 'add_keyword',
  PAUSE_KEYWORD = 'pause_keyword',
  ADD_NEGATIVE_KEYWORD = 'add_negative_keyword',
  ADD_NEGATIVE_KEYWORD_FOR_CAMPAIGN = 'add_negative_keyword_for_campaign',
  PAUSE_NEGATIVE_KEYWORD = 'pause_negative_keyword',
  SET_BUDGET = 'set_budget',
  SET_TCPA = 'set_tcpa',
  ADD_EXACT_MATCH_KEYWORD = 'add_exact_match_keyword',
  CREATE_CAMPAIGN = 'create_campaign',
  CREATE_INTENT_CAMPAIGN = 'create_intent_campaign',
  CREATE_KEYWORD = 'create_keyword',
  CREATE_HEADLINE = 'create_headline',
  CREATE_DESCRIPTION = 'create_description',
  UPDATE_KEYWORD_BID = 'update_keyword_bid',
  UPDATE_KEYWORD_MATCH_TYPE = 'update_keyword_match_type',
  ADD_ALTERNATE_LOCATION = 'add_alternate_location',
  CREATE_CAMPAIGN_WITH_ALTERNATE_LOCATION = 'create_campaign_with_alternate_location',
  SYNC_CAMPAIGN_DATA = 'sync_campaign_data',
  GOOGLE_ADS_SYNC = 'google_ads_sync',
  BUILD_LANDING_PAGE = 'build_landing_page',
}

export interface ActionFilter {
  action_type?: ActionType;
  is_undone?: boolean;
  start_date?: string;
  end_date?: string;
  campaign_id?: string;
  audit_status?: 'pending';
  organization_id?: number;
}

export interface PaginationParams {
  skip: number;
  limit: number;
}

export interface ActionLog {
  // Core fields (from backend API)
  id: number;
  campaign_id: number;
  campaign_name?: string; // Optional as backend may not always provide it
  action_type: ActionType;
  created_at: string;
  is_undone: boolean;
  details?: ActionDetails;

  // State fields (properly typed)
  before_state?: KeywordState;
  after_state?: AfterState;

  // UI enhancement fields
  campaign?: {
    id?: number;
    name?: string;
    slug?: string;
  };
  campaign_slug?: string;
  ad_url?: string;
  ad_slug?: string;

  // Expansion fields (for keyword grouping)
  _expandedId?: string;
  _keywordData?: KeywordState;

  // Metadata
  user_id?: number;
  user_name?: string;
  log?: string;
  reason?: string;
  entity_id?: number;
  entity_type?: string;
  recommendation_ids?: number[];
  undone_at?: string;

  // Optimization review fields (when audit_status=pending)
  campaign_goal?: string;
  business_context?: BusinessContext;

  // Review audit trail — null means unreviewed
  audit_results?: {
    decision?: string;
    reviewed_by?: string;
    reviewed_at?: string;
  } | null;

  // Approval gating: 'recommendation' for PROPOSED items from Colossus review mode
  source?: 'actionlog' | 'recommendation';

  // Cross-platform fields (#4284): Meta rows carry meta_campaign_id + platform='meta' (campaign_id NULL);
  // Google rows carry campaign_id + platform='google'.
  platform?: 'google' | 'meta';
  meta_campaign_id?: number | null;
}

// Business context for optimization review
export interface BusinessContext {
  brand_name?: string;
  additional_context?: Record<string, string[]>;
  ideal_customer?: string;
  products?: string[];
  competitors?: string[];
  business_url?: string;
}

// V3 algorithm analysis from after_state
export interface AfterState extends KeywordState {
  matched_rules?: MatchedRule[];
  // Reverse sync fields (stored by backend action_logging.py)
  keyword?: string;
  negative_keyword?: string;
  asset_text?: string;
  asset_type?: string;
  status?: string;
}

export interface MatchedRule {
  algorithm_version?: string;
  confidence_level?: number;
  priority?: number;
  tags?: string[];
  reason?: string;
  campaign_type?: string;
  final_score?: number;
  component_scores?: Record<string, ComponentScore>;
  llm_analysis?: LLMAnalysis;
  performance_metrics?: PerformanceMetrics;
}

export interface ComponentScore {
  score: number;
  confidence: number;
  reasoning: string;
}

export interface LLMAnalysis {
  is_competitor: boolean;
  is_brand_name: boolean;
  is_location_search: boolean;
  location?: string;
  country?: string;
  competitor_url?: string;
  competitor_closeness: number;
  confidence: number;
  reasoning: string;
}

export interface PerformanceMetrics {
  cost: number;
  impressions: number;
  clicks: number;
  conversions: number; // Last-click attribution conversions
  all_conversions?: number; // All conversions (includes view-through, cross-device, etc.)
  all_conversions_value?: number; // Total value of all conversions
  view_through_conversions?: number; // View-through conversions
  cpc: number;
  cvr: number;
}

export interface AuthResponse {
  token: string;
  user?: UserProfile;
}

export interface InvitationResponse {
  message: string;
  invitation_id?: number;
}

export interface TcpaRegenerationResponse {
  target_cpa: number;
  target_cpa_explanation: string;
}

export interface ProcessRecommendationsResponse {
  processed_count: number;
  message: string;
}

export interface KeywordOperationResponse {
  success: boolean;
  message: string;
  affected_count: number;
}

export interface ActionLogResponse {
  items: ActionLog[];
  total: number;
}

export interface CampaignActionsResponse {
  items: ActionLog[];
  total: number;
  page: number;
  page_size: number;
  campaign: {
    id: number | null;
    name: string;
    slug: string;
  };
}

export interface NotificationMetadata {
  campaign_id?: number;
  campaign_name?: string;
  campaign_slug?: string;
  action_type?: string;
  recommendation_id?: number;
  entity_id?: number;
  entity_type?: string;
  link_url?: string;
  [key: string]: string | number | boolean | undefined;
}

export interface NotificationItem {
  id: string;
  title: string;
  subtitle?: string;
  content: string;
  item_metadata: NotificationMetadata;
  source: string;
  warning_level: 'INFO' | 'WARNING' | 'ERROR' | 'SUCCESS';
  is_viewed: boolean;
  is_archived: boolean;
  created_at: string;
  updated_at: string;
  user_feedback?: 'thumbs_up' | 'thumbs_down' | null;
  feedback_reasons?: string[];
  feedback_text?: string;
  user_account_id?: number | null;
}

export interface NotificationFilter {
  organization_id?: number;
  is_viewed?: boolean;
  is_archived?: boolean;
  sources?: string[];
  warning_levels?: string[];
  start_date?: string;
  end_date?: string;
  search_query?: string;
}

export interface NotificationResponse {
  items: NotificationItem[];
  total: number;
  page: number;
  page_size: number;
}

export type BulkNotificationAction = 'mark_read' | 'mark_unread' | 'archive' | 'unarchive';

// Report types
export type ReportType =
  | 'WEEKLY_PERFORMANCE'
  | 'MONTHLY_SUMMARY'
  | 'QUARTERLY_REVIEW'
  | 'STRATEGIC_6MONTH'
  | 'COMPETITOR_REPORT'
  | 'INTELLIGENCE'
  | 'CUSTOM_SHORT'
  | 'CUSTOM_LONG';

export interface Report {
  id: number;
  report_type: ReportType;
  report_period_start: string;
  report_period_end: string;
  content: string;
  report_metadata: Record<string, unknown>;
  created_at: string;
  updated_at?: string;
}

export interface NotificationCounts {
  by_source: Record<string, number>;
  by_warning_level: Record<string, number>;
  unread_count: number;
  total_count: number;
}

export interface NotificationGroupData {
  source: string;
  notifications: NotificationItem[];
  unread_count: number;
  total_count: number;
  is_expanded: boolean;
}

export interface DatePreset {
  label: string;
  start_date: string;
  end_date: string;
}

export enum PlanType {
  FREE = 'FREE',
  SMB = 'SMB',
  ENTERPRISE = 'ENTERPRISE',
  CUSTOM = 'CUSTOM',
}

export enum PlanPeriod {
  MONTHLY = 'MONTHLY',
  YEARLY = 'YEARLY',
  FOREVER = 'FOREVER',
}

export enum UserRole {
  ADMIN = 'ADMIN',
  USER = 'USER',
}

//member role of an organization
export enum MembershipRole {
  OWNER = 'owner',
  ADMIN = 'admin',
  MEMBER = 'member',
}

//Status of a member in an organization
export enum MembershipStatus {
  ACTIVE = 'active', //active status is for when a user has accepted an invitation to an organization
  PENDING = 'pending', //pending status is for when a user has been invited to an organization but has not accepted the invitation
  DISABLED = 'disabled', //disabled status is for when a user has been removed from an organization
}

export enum FunnelStage {
  UPPER_FUNNEL = 'upper_funnel',
  MIDDLE_FUNNEL = 'middle_funnel',
  LOWER_FUNNEL = 'lower_funnel',
}

export enum CAMPAIGN_TYPE {
  BRAND = 'brand',
  PROSPECTING = 'prospecting',
  COMPETITOR = 'competitor',
  HIPO = 'hipo',
  ICP = 'icp',
  LOCATION = 'location',
  FLIGHTED = 'flighted',
  // Legacy types (deprecated). The literal 'halo' is no longer in this enum —
  // it was renamed to 'hipo' (see #4241). Hard-coded 'halo' lives on in
  // ``HIDDEN_TYPES`` in hooks/useCampaignSidebarFilters.ts as a tombstone so
  // any pre-rename campaigns stay hidden.
  ONBOARDING = 'onboarding',
  FOCUSED = 'focused',
  STANDARD = 'standard',
}

export function getCampaignType(campaign: { goal?: string } | null): CAMPAIGN_TYPE {
  if (!campaign?.goal) return CAMPAIGN_TYPE.PROSPECTING;
  return campaign.goal as CAMPAIGN_TYPE;
}

export const CAMPAIGN_TYPE_LABELS: Record<string, string> = {
  brand: 'Brand',
  prospecting: 'Prospecting',
  competitor: 'Competitor',
  hipo: 'High Performance',
  icp: 'ICP',
  location: 'Location',
};

export function getCampaignTypeLabel(goal: string | undefined): string {
  if (!goal) return '';
  return CAMPAIGN_TYPE_LABELS[goal] ?? (goal.charAt(0).toUpperCase() + goal.slice(1));
}

/**
 * Meta sibling of {@link CAMPAIGN_TYPE_LABELS} (#4288 Phase 4b). Backend Meta enum values stay
 * `TESTING / SCALING / RETARGETING`; users see the demo-aligned `Prospecting / HiPo / Retargeting`
 * vocabulary that Dave runs the rollout on. Same display strings as Google's `'prospecting'` /
 * `'hipo'` entries — they're kept as separate maps because the source enums differ.
 */
export const META_CAMPAIGN_TYPE_LABEL: Record<string, string> = {
  TESTING: 'Prospecting',
  SCALING: 'High Performance',
  RETARGETING: 'Retargeting',
};

// Backend label that `MetaNamingService` appends to campaign names — used by
// `metaCampaignDisplayName` to find the suffix it should swap.
const META_BACKEND_SUFFIX_LABEL: Record<string, string> = {
  TESTING: 'Testing',
  SCALING: 'Scaling',
  RETARGETING: 'Retargeting',
};

/**
 * Swap the backend's trailing `| Testing / | Scaling / | Retargeting` suffix on a Meta campaign
 * name with its demo-aligned counterpart (#4288 Phase 4b). Backend names arrive in the shape
 * `"Acme | acme.com | Scaling"`; users see `"Acme | acme.com | HiPo"`. No Google counterpart —
 * Google names are user-set, no suffix translation needed. Narrow swap on purpose: legacy / hand-
 * edited names that don't end with the expected suffix pass through unchanged.
 */
export function metaCampaignDisplayName(name: string, campaignType: string): string {
  const backendLabel = META_BACKEND_SUFFIX_LABEL[campaignType];
  const displayLabel = META_CAMPAIGN_TYPE_LABEL[campaignType];
  if (!backendLabel || !displayLabel) return name;
  const suffix = new RegExp(`\\|\\s*${backendLabel}\\s*$`);
  return suffix.test(name) ? name.replace(suffix, `| ${displayLabel}`) : name;
}

export type OrganizationCreate = {
  name: string;
};

export interface OrganizationData {
  id: number;
  slug: string;
  name: string;
  plan_type: PlanType;
  stripe_customer_id?: string;
  stripe_subscription_id?: string;
  plan_period: PlanPeriod;
  last_upgrade_date: string | null;
  last_cancel_date: string | null;
  max_spend: number | null;
  default_currency?: string | null;
  // Queue API and MCP writes for human sign-off (glitchads#6613). Owner-only to change; optional because
  // responses predating the column, and the places that build an organization by hand, carry no value.
  approval_required?: boolean;
  onboarding?: {
    business_type?: string;
    advertising_audience?: string;
    current_advertising_spend?: string;
    currency?: string;
    // The Setup page's ticked steps and sub-steps, by id (#6708): kept here so
    // they survive leaving the page and a reload.
    setup_checklist?: SetupChecklist | null;
  } | null;
  created_at: string;
}

export interface SetupChecklist {
  steps: string[];
  children: string[];
}

export interface Membership {
  status: MembershipStatus; // e.g., ACTIVE, PENDING, REJECTED
  role: MembershipRole; // e.g., ADMIN, MEMBER
  last_selected_googleads_account_name?: string | null;
  last_selected_googleads_account_id?: string | null;
}

export interface OrganizationDataWithMembership extends OrganizationData {
  membership: Membership;
  user_count?: number;
  campaign_count?: number;
  primary_logo_url?: string | null;
}

export interface UserAccountWithMembership extends User {
  id?: number | null; // User ID from backend (null for pending invitations)
  picture?: string | null; // User profile picture from backend
  membership: Membership;
  date_added: string; // ISO date string from backend
}
export interface OrganizationDataWithUsersMembership extends OrganizationDataWithMembership {
  users?: UserAccountWithMembership[];
}

export interface PlatformAllocation {
  budget: number;
  campaign_total_monthly: number;
  campaign_total_daily: number;
  remaining_monthly: number;
  remaining_daily: number;
  utilization_pct: number;
  campaign_count: number;
}

export interface BudgetStatusResponse {
  max_spend: number | null;
  platform_budgets: Array<{ platform: string; amount: number }>;
  allocated: Record<string, PlatformAllocation>;
}

export type CampaignCreate = {
  slug: string;
  website_url: string;
  business_desc?: string;
  goal: string;
  target_locations: Location[];
  campaign_goal_text?: string;
  budget?: number;
  organization_id?: number | null;
  business_id?: number;
  target_roas?: number;
  bidding_strategy_type?: string;
  ad_product_ids?: string[];
  ad_landing_page_urls?: string[];
  include_homepage_ad?: boolean;
};

// Competitor Campaign Types
export type CompetitorCampaignCreate = {
  slug: string;
  website_url: string;
  competitor_ids: string[]; // Required, min 1
  high_intent_competitor_ids?: string[]; // Optional, max 3
  business_context?: {
    icp?: string;
    differentiation?: string;
  };
  target_locations: Location[];
  budget?: number;
  goal?: string; // Will be 'competitor'
  organization_id?: number;
  business_id?: number;
};

export type CampaignCompetitorInfo = {
  competitor_id: string;
  competitor_brand_name: string;
  competitor_closeness: number;
  is_high_intent: boolean;
};

export type CompetitorCampaignResponse = {
  message: string;
  campaign_id: number;
  task_tracking_id: number;
  competitors_count: number;
  high_intent_count: number;
  competitors: CampaignCompetitorInfo[];
};

export type AddCompetitorsToCampaignResponse = {
  message: string;
  campaign_id: number;
  added: string[];
  removed: string[];
  skipped: string[];
  deactivated?: string[];
  keywords_added: number;
  keywords_removed: number;
};

// Industry with regulation tier
export type BusinessIndustry = {
  value: string;
  label: string;
  tier: 'highly_regulated' | 'restricted' | 'standard';
};

// Brand identity: archetypes, voice, differentiators
export type BrandArchetypes = {
  primary: string;
  secondary: string | null;
};

export type BrandVoice = {
  formality: number; // 1-5: Formal to Casual
  humor: number; // 1-5: Serious to Funny
  respect: number; // 1-5: Respectful to Irreverent
  enthusiasm: number; // 1-5: Matter-of-fact to Enthusiastic
  technicality: number; // 1-5: Technical to Simple
};

export type BrandIdentity = {
  archetypes: BrandArchetypes;
  voice: BrandVoice;
  differentiators: string[];
};

// Ideal Customer Profile (ICP)
export type IcpFirmographics = {
  industries: string[];
  company_size: string;
  revenue: string;
};

export type IcpDemographics = {
  age_range: string;
  gender: string;
  income_range: string;
  location: string;
};

export type IcpPgob = {
  pains: string[];
  goals: string[];
  objections: string[];
  buying_triggers: string[];
};

export type Icp = {
  id: string;
  name: string;
  title: string;
  market_types: ('B2B' | 'B2C')[];
  firmographics: IcpFirmographics;
  demographics: IcpDemographics;
  pgob: IcpPgob;
};

// Product data
export type ProductData = {
  id: string;
  name: string;
  description: string;
  market_types: ('B2B' | 'B2C' | 'B2B2C')[];
  features?: string[];
  landing_page_url: string | null;
};

// Product-ICP link
export type ProductIcpLink = {
  product_id: string;
  icp_id: string;
};

// Platform budget
export type PlatformBudget = {
  platform: 'Google' | 'Meta' | 'LinkedIn';
  amount: number;
};

// Business settings
export type BusinessSettings = {
  sells_services: boolean;
  brand_adherence_mode: 'strict' | 'discovery';
  llm_instructions: string | null;
};

export type Business = {
  id: number;
  business_url: string;
  budget: number; // DEPRECATED: Use platform_budgets
  ideal_customer: string; // DEPRECATED: Use icps
  business_desc: string;
  brand_name: string | null;
  competitors: string[];
  products: string[]; // DEPRECATED: Use products_data
  additional_context: Record<string, any> | null;
  user_account_id: number;
  organization_id: number | null;
  googleadsaccount_id: number | null;
  target_locations: Location[];
  onboarding_links: string[];
  created_at: string;
  updated_at: string;
  // New fields
  industry: BusinessIndustry | null;
  trust_signals: string[] | null;
  brand_identity: BrandIdentity | null;
  icps: Icp[] | null;
  products_data: ProductData[] | null;
  product_icp_links: ProductIcpLink[] | null;
  platform_budgets: PlatformBudget[] | null;
  settings: BusinessSettings | null;
  goals: string | null;
  biggest_challenge: string | null;
  // Set when a backfill hit the row cap; null otherwise.
  backfill_truncated_at?: string | null;
};

export type BusinessCreate = {
  business_url: string;
  business_desc: string;
  competitors: string[];
  competitors_data?: CompetitorData[] | null; // Full competitor data objects for immediate storage
  products: string[];
  ideal_customer: string;
  brand_name?: string;
  target_locations: Location[];
  budget: number;
  ad_pages: string[];
};

/**
 * A geo-target catalogue row (Google Ads geo_target_constant), keyed by
 * `criteria_id`. This is the shape a campaign's `target_locations` and location
 * exclusions hold.
 *
 * Model note (#4642): this is distinct from the backend `BusinessLocation`
 * model, which wraps a location in a *variant* (global / country / admin /
 * radius with lat-lng) for the Business profile. That variant concept has no
 * representation on a campaign target, which is why the campaign location
 * pickers expose only the Countries & Regions browse/drill-down and omit the
 * Business "Add location" dialog's Worldwide and Radius tabs.
 */
export type Location = {
  canonical_name: string;
  country_code: string;
  criteria_id: number;
  name: string;
  parent_id: number;
  reach: number;
  status: 'Active' | 'Removal Planned';
  target_type: string;
  /** #4497: set by /locations/by-country so the picker can show a drill-down chevron. */
  has_children?: boolean;
};

export interface ExcludedLocation {
  criteria_id: number;
  name: string;
  canonical_name: string;
  country_code: string;
  target_type: string;
}

export interface ExcludedLocationListResponse {
  locations: ExcludedLocation[];
  count: number;
}

export enum ACTION_STATE {
  APPROVED = 'approved',
  UPDATED = 'updated',
}

export enum APPROVAL_STATE {
  APPROVED = 'approved',
  DRAFT = 'draft',
}

export enum CAMPAIGN_STATE {
  LIVE = 'live',
  DRAFT = 'draft',
  ERROR = 'error',
  PAUSED = 'paused',
  CREATING = 'creating',
}

export interface CampaignStateUpdateAccepted {
  message: string;
  task_tracking_id: number;
}

export interface DriftChange {
  entity_type: string;
  change_type: string;
  field_name?: string;
  old_value?: unknown;
  new_value?: unknown;
  keyword_text?: string;
  match_type?: string;
  asset_text?: string;
  ad_url?: string;
  ip_address?: string;
}

export class CampaignDriftError extends Error {
  changes: DriftChange[];
  constructor(detail: string, changes: DriftChange[]) {
    super(detail);
    this.name = 'CampaignDriftError';
    this.changes = changes;
  }
}

/** A Google service connected per organisation, separately from Google Ads (#6879). */
export type GoogleService = 'google_analytics' | 'tag_manager';

/** What the Google consent popup asks for (#6879): Google Ads (required), plus Google Analytics and Tag Manager
 * (optional) while their flag is on. The backend owns the list and the flag. */
export interface GoogleConsentScopes {
  scope: string;
  /** Google Analytics and Tag Manager are asked for and shown on Integrations; off until Google verifies them. */
  analytics_tag_manager_enabled: boolean;
}

/** Connect Google on Integrations, plus each service row's own Connect. */
export interface GoogleIntegrationScopes extends GoogleConsentScopes {
  service_scopes: Record<GoogleService, string>;
}

/** An organization's Google Analytics or Tag Manager connection (#6879). `checking`: connected, and the first
 * listing of what it reaches has not finished. `no_access`: connected, but Google refused it or it reaches nothing. */
export type GoogleServiceState = 'not_connected' | 'checking' | 'no_access' | 'connected';

/** A GA4 property or Tag Manager container the connection's Google account reaches. */
export interface GoogleServiceResource {
  resource_name: string;
  display_name: string;
  parent_display_name: string;
  /** GA4: `edit` or `read`. Tag Manager: null (read-only access cannot see the user's own permission). */
  access_level: string | null;
  /** Tag Manager: the public container id, GTM-XXXX. */
  public_id: string | null;
  /** GA4: linked to the organization's Google Ads account or its manager; null when unknown. */
  linked_to_google_ads: boolean | null;
}

export interface GoogleServiceStatus {
  service: GoogleService;
  label: string;
  state: GoogleServiceState;
  google_email: string | null;
  connected_at: string | null;
  last_checked_at: string | null;
  problem: string | null;
  linked_to_google_ads: boolean | null;
  /** The signed-in user made this connection; null when there is none. */
  connected_by_you: boolean | null;
  /** Google no longer honours the stored access, so only connecting again restores it (a login reaching nothing is not this). */
  reconnect_required: boolean;
  resources: GoogleServiceResource[];
}

/** GET /google/integrations/status for the active organization. */
export interface IntegrationsGoogleServices {
  organization_id: number;
  services: GoogleServiceStatus[];
  /** The user has Google Ads connected and the organization has neither Analytics nor Tag Manager. */
  reconnect_suggested: boolean;
}

/** Envelope from DELETE /google/integration (#5936) — mirrors MetaIntegrationResponse. */
export interface GoogleIntegrationResponse {
  message: string;
  success: boolean;
}

/** What a person-initiated check of the selected Google Ads account found (#6612). ``already_running``: another
 * check of the account was in progress, so nothing was read. */
export interface ExternalCampaignDiscoveryResponse {
  discovered: number;
  already_running: boolean;
}

/** Result of POST /google/accounts/refresh-if-stale (#4207): refreshed=true means Google was
 * actually re-enumerated (the stored list may have changed); skipped_reason names why not. */
export interface GoogleAdsAccountsSilentRefreshResponse {
  refreshed: boolean;
  accounts_found: number;
  skipped_reason: 'fresh' | 'no_token' | 'connection_broken' | null;
}

/** 409 `google_ads_connection_broken` (#5935): the Google refresh token was revoked and the
 * save was refused (or failed) — only reconnecting Google Ads fixes it. Typed like
 * CampaignDriftError so save flows can branch to a reconnect message instead of a raw error. */
export class GoogleAdsConnectionBrokenError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'GoogleAdsConnectionBrokenError';
  }
}

export enum GOOGLEADS_CAMPAIGN_STATE {
  LIVE = 'live',
  DRAFT = 'draft',
  ERROR = 'error',
  PAUSED = 'paused',
  REMOVED = 'removed',
}
export type GoogleAdsAccount = {
  id: string;
  currency: string;
  googleads_account_id: string;
  manager_account_id: string | null;
  name: string;
  selected: false;
  linked: string;
  user_account_id: number;
  nextStep?: string;
  is_allowed_to_publish?: boolean;
};
export type LocationTargetingMode = 'PRESENCE' | 'PRESENCE_OR_INTEREST';

export type GeoTargetingModeResponse = {
  message: string;
  positive_geo_target_type: LocationTargetingMode;
  negative_geo_target_type: LocationTargetingMode;
};

export type CampaignData = {
  business_description: string;
  goal: string;
  slug: string;
  name: string;
  state: CAMPAIGN_STATE;
  website_url: string;
  business_desc: string;
  target_cpa_explanation: string;
  id: number;
  target_locations: Location[];
  target_cpa: number;
  target_roas?: number;
  bidding_strategy_type?: string;
  budget: number;
  phone_numbers: { id: number; value: string; call_extension_enabled: boolean; country_code?: string }[];
  ads: Ad[];
  site_links: SiteLink[];
  callouts: CalloutType[];
  account_callouts?: AccountCallout[]; // Account-level callouts inherited by this campaign
  account_sitelinks?: AccountSitelink[]; // Account-level sitelinks inherited by this campaign (read-only)
  image_extensions?: CreativeAsset[]; // Image extensions (AD_IMAGE) attached to this campaign (#4376)
  structured_snippets: StructuredSnippetType[];
  prices: PriceType[];
  promotions?: PromotionType[];
  settings_state: APPROVAL_STATE;
  assets_state: APPROVAL_STATE;
  budget_state: APPROVAL_STATE;
  errors: CampaignError[];
  user_account_id: number;
  googleads_account_id: number;
  googleadsaccount_id: number;
  organization_id: number;
  googleads_campaign: GoogleCampaignLink;
  googleads_account: GoogleAdsAccount;
  url: string;
  created_at: string;
  first_published_at?: string;
  business_id?: number;
  origin?: 'glitch' | 'external';
  /** External campaigns only (#6381): set when the user imported the campaign's
   * Google Ads structure; the reverse sync then keeps it fresh. */
  structure_imported_at?: string | null;
  /** External campaigns only (#6381): the classifier's verdict. Applied to `goal`
   * automatically above the backend confidence threshold; below it, shown as a
   * suggestion the user confirms or overrides via the campaign type select. */
  suggested_goal?: string | null;
  classification_confidence?: number | null;
  /** External campaigns only (#6381 chunk D): set when adoption completed (imported
   * structure + user-confirmed type). Managed campaigns are writable and optimized. */
  managed_at?: string | null;
  is_deleted?: boolean;
  positive_geo_target_type?: LocationTargetingMode;
  negative_geo_target_type?: LocationTargetingMode;
  last_reverse_synced_at?: string;
  last_reverse_sync_change_count?: number;
  proximity_targets?: CampaignProximityTarget[];
};

// Read shape returned in CampaignData: the campaign's linked subset of the business
// radius catalog. The full catalog is fetched separately to drive select/deselect.
export type CampaignProximityTarget = {
  latitude: number;
  longitude: number;
  radius_km: number;
  address: string | null;
};

// Write shape accepted by CampaignUpdate.proximity_targets. Omitting the field leaves
// radii unchanged; `[]` clears them; a non-empty array full-replaces (customizes) them.
export type CampaignProximityInput = {
  address?: string;
  latitude?: number;
  longitude?: number;
  radius_km: number;
};

export type GoogleCampaign = {
  id: string;
  name: string;
};
export type GoogleCampaignLink = {
  campaign_id: number;
  googleads_campaign_id: string;
  advertising_channel_type?: string;
  /** The campaign's end date in Google Ads, in the account's own time zone (#6622). Google leaves a finished
   * campaign at status ENABLED and derives "Ended" from this date, so the sidebar does the same. */
  end_date?: string | null;
};
export type CampaignError = { error_type: string; error_data?: AdBase };

export type CampaignUpdate = {
  slug: string;
  name: string;
  state: CAMPAIGN_STATE;
  website_url: string;
  business_desc: string;
  id: number;
  target_locations: Location[];
  target_cpa: number;
  target_roas?: number | null;
  bidding_strategy_type?: string;
  target_cpa_explanation: string;
  settings_state: APPROVAL_STATE;
  assets_state: APPROVAL_STATE;
  budget_state: APPROVAL_STATE;
  phone_numbers?: { id: number; value: string; call_extension_enabled: boolean; country_code?: string }[];
  budget?: number;
  proximity_targets?: CampaignProximityInput[] | null;
};

export type BudgetUpdate = {
  budget: number;
  target_cpa?: number;
  bidding_strategy_type?: string;
  approve?: boolean;
};

export enum AD_STATE {
  APPROVED = 'APPROVED',
  DRAFT = 'DRAFT',
  ERROR = 'ERROR',
}
export type AdBase = {
  url: string;
  slug: string;
  state: AD_STATE;
};

export type ScrapedData = {
  id: number;
  title: string;
  description: string;
  url: string;
  content: string;
  links?: string[];
  expire_date: string;
};

export type Ad = {
  id: number;
  slug: string;
  name?: string | null;
  state: string;
  url: string;
  is_paused: boolean;
  campaign_id: number;
  headlines: Headline[];
  descriptions: Description[];
  keywords: Keyword[];
  paused_keywords?: Keyword[];
  negative_keywords?: Keyword[];
  alternative_keywords: AlternativeKeyword[];
  ad_intents?: AdIntent[];
  scraped_data?: ScrapedData;
  funnel_stage?: string;
  ad_funnel_stage?: AdFunnelStage[];
  ad_group_id?: number;
  /** #4407: Pre-generated Google Ads ad group name. Set at ad creation so the
   * UI can show the exact name pre-publish. NULL for pre-migration ads. */
  googleads_ad_group_name?: string | null;
  googleads_ad_group?: {
    id?: number;
    googleads_ad_group_id: string;
    googleads_campaign_id?: string;
    status?: string | null;
    name?: string | null;
  } | null;
  labels?: AdLabel[];
};

export type AdLabel = {
  id: string;
  name: string;
  resource_name: string;
};

export type AdFunnelStage = {
  id?: number;
  funnel: FunnelStage | string;
  is_paused?: boolean;
};

export type HeadlineType =
  | 'brand'
  | 'benefit'
  | 'cta'
  | 'feature'
  | 'usp'
  | 'social_proof'
  | 'keyword';

export type Headline = {
  id?: number;
  text: string;
  ad_funnel_stage?: AdFunnelStage;
  pin_position?: number | null; // 1, 2, or 3; null means not pinned
  is_locked?: boolean; // true means optimization algorithms cannot delete this headline
  type?: HeadlineType | null;
  is_deleted?: boolean; // staged delete: hidden in the editor, paused by the backend on save
};

export type Description = {
  id?: number;
  text: string;
  ad_funnel_stage?: AdFunnelStage;
  is_deleted?: boolean; // staged delete: hidden in the editor, paused by the backend on save
};

export type SiteLink = {
  url: string;
  id?: number;
  name: string;
  description1: string;
  description2: string;
};

export type SiteLinkCreateOrUpdate = {
  url: string;
  id?: number;
  name: string;
  description1: string;
  description2: string;
};

export interface AccountCallout {
  customer_asset_resource_name: string;
  asset_resource_name: string;
  callout_text: string;
  status: string;
}

export interface AccountCalloutListResponse {
  callouts: AccountCallout[];
  count: number;
}

export interface AccountSitelink {
  uuid: string;
  customer_asset_resource_name: string;
  asset_resource_name: string;
  name: string;
  url: string;
  description1: string;
  description2: string;
  status: string;
}

export interface AccountSitelinkListResponse {
  sitelinks: AccountSitelink[];
  count: number;
}

/** A sitelink set on campaigns rather than on the account (#6745). */
export interface CampaignSitelink {
  id: number;
  name: string;
  url: string;
  description1: string;
  description2: string;
  campaigns: { id: number; slug: string; name: string }[];
}

export interface CampaignSitelinkListResponse {
  sitelinks: CampaignSitelink[];
  count: number;
}

/** A callout set on campaigns rather than on the account (#6745). */
export interface CampaignCallout {
  id: number;
  text: string;
  campaigns: { id: number; slug: string; name: string }[];
}

export interface CampaignCalloutListResponse {
  callouts: CampaignCallout[];
  count: number;
}

/**
 * An address excluded by one or more of the organization's campaigns (#6745),
 * rolled up across all of them. Not to be confused with apis.ts's
 * CampaignIpExclusion, which is a single campaign's own row.
 */
export interface OrgCampaignIpExclusion {
  ip_address: string;
  campaigns: { id: number; slug: string; name: string }[];
}

export interface OrgCampaignIpExclusionListResponse {
  exclusions: OrgCampaignIpExclusion[];
  count: number;
}

export interface NegativeKeywordList {
  id: string;
  resource_name: string;
  name: string;
  member_count: number;
  applied_campaign_count: number;
}

export interface NegativeKeywordListsResponse {
  lists: NegativeKeywordList[];
  count: number;
}

export interface SharedNegativeKeyword {
  resource_name: string;
  criterion_id: string;
  keyword: string;
  match_type: string;
}

export interface SharedNegativeKeywordsResponse {
  keywords: SharedNegativeKeyword[];
  count: number;
}

export interface AppliedCampaign {
  googleads_campaign_id: string | null;
  campaign_id: number | null;
  campaign_name: string;
}

export interface AppliedCampaignsResponse {
  campaigns: AppliedCampaign[];
  count: number;
}

export interface IpExclusion {
  resource_name: string;
  ip_address: string;
}

export interface IpExclusionListResponse {
  exclusions: IpExclusion[];
  count: number;
}

export interface CampaignLanguage {
  language_code: string;
  name: string;
}

export interface CampaignLanguageListResponse {
  languages: CampaignLanguage[];
  count: number;
}

export type CalloutType = {
  id?: number;
  text: string;
};

export type StructuredSnippetType = {
  id?: number;
  header_type: string;
  values: { value: string }[];
};

export type PriceType = {
  id?: number;
  type: string;
  currency: string;
  qualifier: string;
  header: string;
  value: number;
  frequency: string;
  description: string;
  final_url: string;
  mobile_url?: string;
};

// Mirrors backend/app/campaign/asset_models.py:328 (PromotionBase + id from PromotionRead).
export type PromotionType = {
  id?: number;
  promotion_target: string;
  discount_type: 'percent off' | 'money off';
  discount_modifier: number;
  currency?: string | null;
  occasion?: string | null;
  promotion_code?: string | null;
  start_date?: string | null;
  end_date?: string | null;
};

// Non-fatal Google Ads rejection returned by asset save endpoints (backend SyncWarning)
export type SyncWarning = {
  asset_type: string;
  error_code: string;
  message: string;
};

export type UserProfile = {
  email?: string;
  picture?: string;
  name?: string;
  role?: UserRole;
  impersonating_email?: string;
  last_organization_id?: number;
  // Returned by GET /user/me. true => user has a GlitchAds password (signed
  // up with email or linked one later). false => Google-only sign-in, no
  // password to change on our side — UI directs them to manage on Google.
  has_password?: boolean;
  // Returned by GET /user/me (#5935). true => the Google refresh token was revoked
  // (invalid_grant); Settings shows the Google Ads card disconnected with a reconnect CTA.
  google_connection_broken?: boolean;
  // Returned by GET /user/me (#5936). false => the user voluntarily disconnected Google
  // Ads (no refresh token held); Settings shows the Connect CTA.
  google_ads_connected?: boolean;
};

export interface GoogleAdsAccountUser {
  user_id: string;
  email_address: string;
  access_role: string;
  access_creation_date: string;
  inviter_email: string;
  can_create_campaigns: boolean;
}

export enum RECOMMENDATION_SEVERITY {
  SUGGESTION = 'suggestion',
  URGENT = 'urgent',
  FIX = 'fix',
}

export enum RECOMMENDATION_STATE {
  PENDING = 'pending',
  INSTANT_FIX_APPLIED = 'instant fix applied',
  MARKED_RESOLVED = 'marked resolved',
  MARKED_FAILED = 'marked failed',
  MARKED_NOT_APPLICABLE = 'marked not applicable',
  FAILED = 'failed',
}

export type Keyword = {
  id?: number;
  text: string;
  is_paused?: boolean;
  is_deleted?: boolean;
  match_type?: string; // EXACT, PHRASE, BROAD
  final_url?: string | null; // keyword-level Final URL override (GA criterion). Absent/empty = uses the ad's URL.
  avg_monthly_searches?: number;
  competition?: string; // LOW, MEDIUM, HIGH
  competition_index?: number; // 0-1
  pin_position?: number | null; // 1, 2, or 3; null means not pinned
  is_locked?: boolean; // true means optimization algorithms cannot delete this keyword
};

export type AlternativeKeyword = Omit<Keyword, 'is_deleted'>;

export type KeywordAIAnalysis = {
  landing_page_relevance_score: number;
  brand_relevance_score: number;
  overall_alignment_score: number;
  grade: 'A' | 'B' | 'C' | 'D';
  recommendation: string;
  insights: string[];
  last_analyzed: string;
};

export type KeywordAlternative = {
  keyword_text: string;
  relevance_score: number;
  reason: string;
};

export type KeywordAIAnalysisResponse = {
  keyword_id: number;
  keyword_text: string;
  metrics: {
    avg_monthly_searches: number;
    competition: string;
    competition_index: number;
    low_top_of_page_bid: number;
    high_top_of_page_bid: number;
    reach_score: number;
    competition_score: number;
    ai_analysis: KeywordAIAnalysis;
    alternatives: KeywordAlternative[];
  };
};

export type AdRecommendation = {
  id: number;
  ad_id: number;
  campaign_recommendation_id: number;
  title: string;
  severity: RECOMMENDATION_SEVERITY;
  description: string;
  state: RECOMMENDATION_STATE;
  created_at: string;
  ad: Ad;
  keyword_recommendations: KeywordRecommendation[];
  recommendation_type: 'underperforming_keyword' | 'new_keyword';
};

export type KeywordRecommendation = {
  id: number;
  title: string;
  severity: RECOMMENDATION_SEVERITY;
  description: string;
  state: RECOMMENDATION_STATE;
  created_at: string;
  keyword: Keyword;
};

export type CampaignRecommendations = {
  recommendations: CampaignRecommendation[];
};

export type CampaignRecommendation = {
  id: number;
  campaign_id: number;
  title: string;
  description: string;
  severity: RECOMMENDATION_SEVERITY;
  state: RECOMMENDATION_STATE;
  created_at: string;
  ad_recommendations: AdRecommendation[];
  recommendation_type: 'underperforming_keyword' | 'new_keyword';
};

export type TaskTrackingSummary = {
  total: number;
  in_progress: number;
  completed: number;
  failed: number;
  stuck_in_progress?: number;  // Count of tasks stuck in IN_PROGRESS state (older than threshold)
  latest_error_message?: string | null;  // Most recent error message from failed tasks
  restart_attempt_count?: number;  // Number of restart attempts (only counts real failures, not killed tasks)
};

export type BusinessTaskTrackingResponse = {
  brand_intel_generation: {
    total: number;
    in_progress: number;
    completed: number;
    failed: number;
  };
  is_generating: boolean;
  latest_error_message?: string | null;
};

export type AllCampaignTaskTrackingResponse = {
  [campaignId: number]: TaskTrackingResponse;
};

export type TaskTrackingResponse = Record<
  | 'ad_generation'
  | 'site_link_generation'
  | 'tcpa_generation'
  | 'keywords_generation'
  | 'headline_generation'
  | 'descriptions_generation'
  | 'campaign_creation'
  | 'campaign_state_update'
  | 'campaign_reverse_sync'
  | 'campaign_ads_ga_sync'
  | 'external_campaign_classification',
  TaskTrackingSummary
> & {
  // Added fields for campaign staleness check
  created_at?: string;
  is_stale?: boolean;
};

export type User = {
  email: string;
  name: string;
  role: UserRole;
  organizations?: string[];
};

// Using the ActionLog interface defined above

export type ButtonVariant = 'link' | 'default' | 'destructive' | 'outline' | 'secondary' | 'ghost';

export interface StatusSectionProps {
  title: string;
  description: string;
  metrics: Array<{
    label: string;
    value: string;
    icon?: React.ReactNode;
  }>;
  className?: string;
}

export interface StatusCardProps extends React.HTMLAttributes<HTMLDivElement> {
  title: string;
  description: string;
  values: (string | number)[];
  icon?: React.ReactNode;
}

export interface SummaryCardProps {
  title?: string;
  content: string[];
  statusCardProps?: StatusCardProps[];
  statusSectionProps?: StatusSectionProps[];
}

export interface NumberBadgeProps {
  number: number;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

export interface StringBadgeProps {
  string: string;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

export type BadgeProps = NumberBadgeProps | StringBadgeProps;

export interface ButtonProps {
  label: string;
  onClick: () => void;
  className?: string;
  variant?: ButtonVariant;
  disabled?: boolean;
  icon?: React.ReactNode;
  iconPosition?: 'left' | 'right';
  style?: string;
}

export interface SectionHeaderProps {
  title: string;
  badgeProps?: BadgeProps;
  buttonProps?: ButtonProps[];
  isSubSection?: boolean;
}

export type Button = {
  label: string;
  onClick: () => void;
  style?: string;
};

export type TaskCardProps = {
  icon?: LucideIcon | string;
  title: string;
  content: string;
  buttons?: Button[];
};

export interface CampaignRegistrationResultOption {
  title: string;
  content: string;
}

export type RegistrationResultProps = {
  type?: 'SUCCESS' | 'ISSUE';
  result: CampaignRegistrationResultOption;
};

export interface TaskSectionProps {
  title: string;
  badge?: NumberBadgeProps | StringBadgeProps;
  tasks?: TaskCardProps[];
  isLast?: boolean;
  approveAll?: () => Promise<void>;
  disableApproveAll?: boolean;
}

export type CampaignGoal = {
  id: string;
  title: string;
  icon: React.ReactNode;
  className: string;
};

export interface Country {
  id: string;
  name: string;
  reach: string;
}

export type Position = 'UP' | 'DOWN';

export interface TargetItem {
  id: string;
  name: string;
}

export interface MultiSelectItemProps {
  selectedItems: TargetItem[];
  onItemAdd: (item: TargetItem) => void;
  onItemRemove: (item_id: string) => void;
}

export interface BudgetInputProps {
  value: number;
  setValue: (value: number) => void;
  currency?: string;
  placeholder?: string;
  cycle?: string;
}

export interface Action {
  label: string;
  onClick: () => void;
  variant?: ButtonVariant;
  icon?: React.ComponentType<React.SVGProps<SVGSVGElement>>;
}

export interface MetricCardProps {
  label: string;
  value: string | number;
}

export type CampaignTabValue = 'overview' | 'ads' | 'assets' | 'settings' | 'budget';
export type TasksTabValue = 'new' | 'logs';
export type SettingsTabValue =
  | 'organization'
  | 'team'
  | 'billing'
  | 'notification'
  | 'privacy'
  | 'integrations'
  | 'reports';

export interface Tab {
  id: CampaignTabValue | SettingsTabValue | TasksTabValue;
  name: string;
  href: string;
}

export interface TabsProps {
  tabs: Tab[];
  currentTab?: string;
  setCurrentTab?: (value: CampaignTabValue | SettingsTabValue | TasksTabValue) => void;
  className?: string;
}

export interface AdCardProps {
  title: string;
  description: string;
  keywords: Keyword[];
  needReview: boolean;
}

export interface UserListProps {
  users: User[];
  selectedUser?: User;
  onUserSelect?: (user: User) => void;
}
export interface Url {
  url: string;
}

export interface AssetsAdCardProps {
  id?: number;
  url: string;
  title: string;
  description: string;
  image: string;
  locations?: string[];
}

export interface Member {
  name: string;
  email: string;
  role: string;
}

export interface Permission {
  id: string;
  name: string;
  description: string;
  required: boolean;
  enabled: boolean;
}

export interface ServiceConfig {
  name: string;
  description: string;
  permissions: Permission[];
}

export interface IntegrationFlowProps {
  service: ServiceConfig;
  onCancel: () => void;
  onConnect: (data: { accountId: string; apiKey: string; permissions: Permission[] }) => void;
}

export interface ConnectionDetailProps {
  connectedDate: string;
  accountId: string;
  apiKey: string;
  serviceName: string;
  /** Field labels; default to 'Account ID' / 'Account Name'. User-level
   * integrations (Meet) show a Google account email, not an account id. */
  accountIdLabel?: string;
  accountNameLabel?: string;
}

import type { BulkAction } from '@/components/glitch-ui/bulk-select';

export type DataTableProps<TData> = {
  title?: string;
  actionButtonText?: string;
  onActionButtonClick?: () => void;
  data: TData[];
  columns: ColumnDef<TData, unknown>[];
  /**
   * Rows per page. Followed, not just seeded: a page that lets the reader choose
   * (Search Terms, #6758) holds the value itself and passes it here, and the
   * table returns to the first page when it changes.
   */
  pageSize?: number;
  /**
   * Rows-per-page the reader may choose, drawn beside the page buttons at the
   * foot of the table (#6758). The page owns the value — pass it as `pageSize`
   * and persist it — so the choice survives leaving the screen.
   */
  pageSizeOptions?: number[];
  onPageSizeChange?: (size: number) => void;
  /**
   * Where the page controls are drawn. 'bottom' by default, as every house table
   * has had them. 'both' repeats them above the rows for a long table, so it can
   * be paged from whichever end the reader is at (#6758).
   */
  paginationPosition?: 'bottom' | 'both';
  onRowClick?: (row: TData) => void;
  itemsName?: string;
  downloadPDF?: boolean;
  initialSorting?: SortingState;
  variant?: 'bordered' | 'flush';
  emptyState?: React.ReactNode;
  emptyIcon?: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  emptyTitle?: string;
  emptyDescription?: string;
  footerContent?: React.ReactNode;
  getRowClassName?: (row: TData) => string | undefined;
  /**
   * Rows can be ticked and acted on (#6724). While `count` is above zero the
   * column headings give way to the count, the actions and Clear, beside the
   * select-all box in the first column. The page owns the selection itself
   * (`useBulkSelection`) and the confirmation each action needs.
   */
  bulk?: {
    count: number;
    /** Singular noun for the count, e.g. "landing page". */
    itemName: string;
    actions: BulkAction[];
    onClear: () => void;
  };
};

export type PaginationProps = {
  pageIndex?: number;
  totalPages?: number;
  pageCount: number;
  canPreviousPage: boolean;
  canNextPage: boolean;
  pageSize: number;
  onPreviousPage: () => void;
  onNextPage: () => void;
  itemsName?: string;
  totalItems: number;
  currentItems: number;
  /** The leading bar says which rows are on show; the trailing one repeats only
   * the controls, so the reader is not told the same thing twice (#6758). */
  showRange?: boolean;
  /** Rows-per-page, drawn at the left of the bar when offered (#6758). */
  pageSizeOptions?: number[];
  onPageSizeChange?: (size: number) => void;
};

export interface Intent {
  id: number;
  name: string;
}

export interface Audience {
  id: number;
  description: string;
}

export interface AdIntent {
  id: number;
  intent_id?: number;
  audience_id?: number;
  intent?: Intent;
  audience?: Audience;
}

export interface PerformanceData {
  date: string;
  clicks: number;
  impressions: number;
  cost: number;
  conversions?: number; // Last-click attribution conversions
  all_conversions?: number; // All conversions (includes view-through, cross-device, etc.)
  all_conversions_value?: number; // Total value of all conversions
  view_through_conversions?: number; // View-through conversions
  ctr?: number; // Click-through rate as percentage
  avgCpc?: number; // Average cost per click
  cpa?: number; // Cost per acquisition/conversion
  // campaign_id: number; campaign_id is unused variable for performance chart.
  bidding_strategy_type?: string; // Bidding strategy type (TARGET_CPA, TARGET_ROAS, MAXIMIZE_CLICKS, etc.)
  bidding_strategy_id?: string; // Bidding strategy ID
}

export interface PerformanceCardData {
  title: string;
  value: number | string;
  performance: number | string;
  performanceType: 'INC' | 'DEC';
  description: string;
}

export type NegativeKeywordUpdate = {
  id?: number | null;
  keyword: string;
  match_type: string;
};

export type AdUpdate = {
  id: number;
  url: string;
  slug: string;
  name?: string | null;
  state: AD_STATE;
  headlines: (Headline & { ad_funnel_stage?: { funnel: string } })[];
  descriptions: Description[];
  keywords: Keyword[];
  negative_keywords?: NegativeKeywordUpdate[];
  labels?: { id?: string; name: string }[];
};

export interface ConversionTrackingSettings {
  conversion_customer_id: string;
  tracking_status: string;
  tracking_id: string;
  cross_account_tracking_id: string;
}

// Conversion Action Types
export interface TrackingSnippet {
  global_site_tag?: string | null;
  event_snippet?: string | null;
  conversion_id?: string | null;
  conversion_label?: string | null;
  conversion_action_id?: string | null;
}

export interface ConversionAction {
  resource_name: string;
  id: string;
  name: string;
  category?: string | null;
  type?: string | null;
  status?: string | null;
  tracking_snippet?: TrackingSnippet | null;
  phone_number?: string | null;
  default_value?: number | null;
  always_use_default_value?: boolean | null;
  view_through_lookback_window_days?: number | null;
  click_through_lookback_window_days?: number | null;
  primary_for_goal?: boolean | null;
  tracking_status?: string | null;
  conversions_last_30d?: number | null;
  all_conversions_last_30d?: number | null;
  registry_id?: number | null;
  source?: string | null;
  funnel_stage?: string | null;
}

export interface ConversionActionListResponse {
  conversion_actions: ConversionAction[];
  count: number;
}

/** A conversion action linked to a campaign (GET /campaigns/{id}/conversion-actions row). */
export interface CampaignConversionLink {
  id: number;
  is_primary: boolean;
  campaign_id: number;
  conversion_action_id: number;
  // Campaign's actual Google Ads conversion-goal scope: "CAMPAIGN" = these actions
  // drive a campaign-specific custom goal, "CUSTOMER" = inherits account defaults (#5456).
  goal_config_level?: string | null;
  conversion_action: {
    id: number;
    name: string;
    source: string;
    is_active: boolean;
    funnel_stage: string | null;
    action_type: string | null;
    default_value: number | null;
    primary_for_goal: boolean | null;
    google_ads_action_id: string;
    conversions_last_30d: number | null;
  } | null;
}

/** Body for PATCH /campaigns/{id}/conversion-actions/{actionId} — omitted fields unchanged (#6479). */
export interface CampaignConversionActionUpdate {
  name?: string;
  default_value?: number;
  primary_for_goal?: boolean;
  funnel_stage?: string;
}

export interface WebsiteConversionCreateRequest {
  name: string;
  category?: string;
  value?: number;
  always_use_default_value?: boolean;
  count_as_conversion?: boolean;
  view_through_lookback_window_days?: number;
  click_through_lookback_window_days?: number;
  attribution_model?: string;
  funnel_stage?: string;
}

export interface CallConversionCreateRequest {
  name: string;
  phone_number?: string;
  caller_id_source?: string;
  category?: string;
  value?: number;
  always_use_default_value?: boolean;
  count_as_conversion?: boolean;
  conversion_count_type?: string;
  funnel_stage?: string;
}

export interface ConversionActionUpdateRequest {
  name?: string;
  category?: string;
  value?: number;
  always_use_default_value?: boolean;
  primary_for_goal?: boolean;
  funnel_stage?: string;
}

export interface Invoice {
  id: string;
  amount_paid: number;
  currency: string;
  status: string;
  created: number;
  invoice_pdf?: string;
}

export interface InvoiceHistoryResponse {
  invoices: Invoice[];
  total_count: number;
  has_more: boolean;
}

export interface CancelSubscriptionRequest {
  at_period_end: boolean;
}

export interface CancelSubscriptionResponse {
  id: string;
  status: string;
  cancel_at_period_end: boolean;
  canceled_at: number | null;
  current_period_end: number | null;
  message: string;
}

// HubSpot Integration Types
export interface HubSpotIntegrationStatus {
  is_connected: boolean;
  account_id?: string;
  account_name?: string;
  scopes?: string[];
  last_sync_at?: string;
  created_at?: string;
}

export interface HubSpotIntegrationResponse {
  success: boolean;
  message: string;
  integration_status?: HubSpotIntegrationStatus;
}

export interface HubSpotOAuthUrlResponse {
  oauth_url: string;
  state: string;
}

export interface HubSpotCallbackRequest {
  code: string;
  state: string;
}

export interface HubSpotStage {
  label: string;
  stage_id: string;
  display_order: number;
}

export interface HubSpotPipeline {
  label: string;
  pipeline_id: string;
  display_order: number;
  stages: HubSpotStage[];
}

export interface HubSpotAuditResponse {
  portal_id: string;
  integration_id: number;
  pipelines: HubSpotPipeline[];
}

// Meta Ads Integration Types
export interface MetaAdAccountInfo {
  /** Local MetaAdAccount row id — present for persisted accounts (the /meta/ad-accounts listing, integration status). */
  id?: number;
  account_id: string;
  name?: string;
  currency?: string;
  timezone_name?: string;
  account_status?: number;
  /** Facebook Page bound to this ad account (#4277 chunk 3). NULL until the user picks one. */
  page_id?: string | null;
  page_name?: string | null;
  /** Meta Pixel bound to this ad account (Phase 7 Chunk 0). NULL until the user picks one. */
  pixel_id?: string | null;
  pixel_name?: string | null;
}

export interface MetaIntegrationStatus {
  is_connected: boolean;
  // True iff the platform killed the connection (revoked token / deauthorize callback, #5949) —
  // drives "Reconnect" wording instead of a first-time "Connect". Always false while connected.
  connection_broken?: boolean;
  meta_user_id?: string;
  name?: string;
  scopes?: string[];
  business_id?: string;
  ad_accounts: MetaAdAccountInfo[];
  created_at?: string;
  last_sync_at?: string;
  // The ad account the current (user, org) has picked via "Use for campaigns". The integration card uses
  // this as its primary identity label (e.g. "Glitch Ads · EUR") so Meta lines up with how Google Ads /
  // HubSpot surface an *account* rather than the connecting human. Null if no membership selection yet.
  selected_ad_account?: MetaAdAccountInfo;
}

export interface MetaIntegrationResponse {
  success: boolean;
  message: string;
  integration_status?: MetaIntegrationStatus;
}

export interface MetaOAuthUrlResponse {
  oauth_url: string;
  state: string;
}

export interface MetaCallbackRequest {
  code: string;
  state: string;
}

export type MetaCampaignType = 'SCALING' | 'TESTING' | 'RETARGETING';
export type MetaEntityStatus = 'ACTIVE' | 'PAUSED' | 'ARCHIVED' | 'DELETED';

/** Meta link-ad CTA button type (#4655). Mirrors the backend `MetaCallToAction` enum. */
export type MetaCallToAction =
  | 'LEARN_MORE'
  | 'SIGN_UP'
  | 'GET_QUOTE'
  | 'CONTACT_US'
  | 'APPLY_NOW'
  | 'SUBSCRIBE'
  | 'DOWNLOAD'
  | 'GET_OFFER'
  | 'SHOP_NOW';

export interface MetaAdResponse {
  id: number;
  name: string;
  meta_ad_id: string;
  primary_text: string;
  created_at: string;
  destination_url: string;
  status: MetaEntityStatus;
  // The image lives in the cross-platform creative-asset library (#4277). Callers that need the
  // bytes or a signed render URL hit GET /assets/{creative_asset_id}.
  creative_asset_id: number;
  headline?: string | null;
  description?: string | null;
  // Hook variants (#6210): ordered, element 0 mirrors the scalar field of the same name.
  // null = the ad predates variants (the scalar is the only hook).
  headlines?: string[] | null;
  descriptions?: string[] | null;
  primary_texts?: string[] | null;
}

/**
 * `PATCH /meta/campaigns/ads/{ad_id}/copy` body (#5981) — inline copy editing on the campaign
 * screen. Any subset of the three fields; omitted fields keep the ad's current copy. The backend
 * rejects a body providing nothing, and rejects blank values (Meta link ads require all three).
 */
/**
 * `PATCH /meta/campaigns/{slug}/targeting` body (#5981) — partial geo-targeting update. Each
 * provided field replaces its slice of the spec; omitted fields stay untouched, so the Locations
 * and Excluded Locations sub-tabs save independently. A provided positive selection must be
 * non-empty; `excluded_location_criteria_ids: []` clears all exclusions.
 */
export interface MetaCampaignTargetingUpdateRequest {
  target_location_criteria_ids?: number[] | null;
  target_radii?: CampaignProximityInput[] | null;
  excluded_location_criteria_ids?: number[] | null;
  locales?: MetaLocaleEntry[] | null;
  audience?: MetaAudienceSelection | null;
  interests?: MetaInterestEntry[] | null;
  placements?: MetaPlacementSelection | null;
}

/**
 * The Placements sub-tab's state (#6076). Empty publisher_platforms = Advantage+ automatic
 * placements (no placement fields written to the spec — never an exhaustive list). Positions are
 * only valid for selected platforms.
 */
export interface MetaPlacementSelection {
  publisher_platforms: string[];
  positions: Record<string, string[]>;
}

/**
 * Static placement catalog with display labels (#6076) — mirrors the backend's
 * META_PLACEMENT_POSITIONS (Meta's documented v25 values; Threads is platform-only). Meta has no
 * Graph search for placements, so both sides carry the catalog.
 */
export const META_PLACEMENT_CATALOG: {
  platform: string;
  label: string;
  positions: { value: string; label: string }[];
}[] = [
  {
    platform: 'facebook',
    label: 'Facebook',
    positions: [
      { value: 'feed', label: 'Feed' },
      { value: 'right_hand_column', label: 'Right column' },
      { value: 'marketplace', label: 'Marketplace' },
      { value: 'video_feeds', label: 'Video feeds' },
      { value: 'story', label: 'Stories' },
      { value: 'search', label: 'Search results' },
      { value: 'instream_video', label: 'In-stream video' },
      { value: 'facebook_reels', label: 'Reels' },
    ],
  },
  {
    platform: 'instagram',
    label: 'Instagram',
    positions: [
      { value: 'stream', label: 'Feed' },
      { value: 'story', label: 'Stories' },
      { value: 'explore', label: 'Explore' },
      { value: 'explore_home', label: 'Explore home' },
      { value: 'reels', label: 'Reels' },
      { value: 'profile_feed', label: 'Profile feed' },
      { value: 'ig_search', label: 'Search results' },
    ],
  },
  {
    platform: 'messenger',
    label: 'Messenger',
    positions: [
      { value: 'messenger_home', label: 'Messenger inbox' },
      { value: 'story', label: 'Stories' },
    ],
  },
  {
    platform: 'audience_network',
    label: 'Audience Network',
    positions: [
      { value: 'classic', label: 'Native, banner and interstitial' },
      { value: 'rewarded_video', label: 'Rewarded video' },
    ],
  },
  { platform: 'threads', label: 'Threads', positions: [] },
];

/** One Meta interest (#6076) — string id + name from the adinterest search, persisted as a pair. */
export interface MetaInterestEntry {
  id: string;
  name: string;
}

/** The event a conversions campaign optimizes toward (#6076) — standard Pixel event or custom conversion. */
export interface MetaOptimizationEvent {
  type: 'standard' | 'custom';
  name: string;
  event?: string | null;
  custom_conversion_id?: string | null;
}

/** One Meta attribution_spec entry (#6076). */
export interface MetaAttributionEntry {
  event_type: 'CLICK_THROUGH' | 'VIEW_THROUGH';
  window_days: number;
}

/** One optimization-event choice from GET /meta/campaigns/conversion-events (#6076). */
export interface MetaConversionEventOption {
  type: 'standard' | 'custom';
  name: string;
  event?: string | null;
  custom_conversion_id?: string | null;
}

/** `PATCH /meta/campaigns/{slug}/conversion-settings` body (#6076) — at least one field required. */
export interface MetaConversionSettingsUpdateRequest {
  optimization_event?: MetaOptimizationEvent;
  // Accepted by the schema but rejected by the service: attribution is creation-time-only on
  // Meta (subcode 1504040). Retained for a future creation-time/recreate flow; do not send.
  attribution_spec?: MetaAttributionEntry[];
}

/**
 * `PATCH /meta/campaigns/{slug}/budget-mode` body (#6211) — switch a campaign between ABO and CBO.
 * CBO requires campaign_daily_budget_cents; ABO requires ad_set_budget_cents covering EVERY active
 * ad set (local id → cents). Meta rate-limits the toggle (~90-minute cooldown).
 */
export interface MetaBudgetModeUpdateRequest {
  is_cbo: boolean;
  campaign_daily_budget_cents?: number;
  ad_set_budget_cents?: Record<number, number>;
}

/**
 * `POST /meta/campaigns/{slug}/ad-sets` body (#6211) — add ONE new ad set to a pushed campaign.
 * Budget only for ABO campaigns (CBO campaigns own the budget and refuse one).
 */
export interface MetaAdSetCreateRequest {
  ads: { creative_asset_id: number }[];
  daily_budget_cents?: number;
}

/** 202 payload for the add-ad-set dispatch — poll `/{slug}/ad-set-task-status`. */
export interface MetaAdSetDispatchResponse {
  task_tracking_id: number;
}

/** `GET /meta/campaigns/ads/{id}/preview` payload (#6213) — Meta's own iframe rendering. */
export interface MetaAdPreviewResponse {
  ad_format: string;
  body: string;
}

/** The preview formats the eye-icon modal offers (#6213) — mirrors META_AD_PREVIEW_FORMATS. */
export const META_AD_PREVIEW_FORMATS: { id: string; label: string }[] = [
  { id: 'DESKTOP_FEED_STANDARD', label: 'Desktop Feed' },
  { id: 'MOBILE_FEED_STANDARD', label: 'Mobile Feed' },
  { id: 'INSTAGRAM_STANDARD', label: 'Instagram Feed' },
  { id: 'INSTAGRAM_STORY', label: 'Instagram Story' },
];

/** One saved custom audience — id is what Meta's spec takes; name persists for display (#6076). */
export interface MetaCustomAudienceRef {
  id: string;
  name: string;
}

/**
 * The Audience sub-tab's complete demographic state (#6076) — replace-whole-object slice: a
 * provided selection IS the new narrowing; null fields reset to Meta's defaults (18–65+, all
 * genders, no saved audiences). genders: [1] = men, [2] = women, omit = all.
 */
export interface MetaAudienceSelection {
  age_min?: number | null;
  age_max?: number | null;
  genders?: number[] | null;
  custom_audiences?: MetaCustomAudienceRef[] | null;
}

/**
 * One Meta language locale (#5981). `key` is the opaque numeric id Meta's targeting spec takes;
 * `name` is the display label from Meta's adlocale search — persisted as a pair on the campaign
 * because Meta offers no key-to-name lookup.
 */
export interface MetaLocaleEntry {
  key: number;
  name: string;
}

export interface MetaAdCopyUpdateRequest {
  headline?: string;
  primary_text?: string;
  description?: string;
  // Hook-variant replacement (#6210): a plural field replaces that field's whole hook list (1-5,
  // element 0 becomes the scalar). Never send a scalar and its plural together.
  headlines?: string[];
  descriptions?: string[];
  primary_texts?: string[];
}

/**
 * Cross-platform creative-asset library (#4277).
 *
 * Two shapes: assets are either uploaded by the user (platform === null, the bytes live at
 * `gs://glitchads-creative-assets/<org>/<uuid>.<ext>`) or AI-generated for a specific platform
 * (the bytes live under `meta/` or `google/`). The DB-level CHECK constraint enforces that
 * (source, platform) pair, so any combination beyond the two above can't reach the frontend.
 */

export type CreativeAssetSource = 'USER_UPLOAD' | 'AI_GENERATED' | 'SCRAPED';
export type TagType = 'system' | 'user';
export type AdPlatform = 'META' | 'GOOGLE';

export interface CreativeAssetTag {
  id: number;
  tag_type: TagType;
  tag_value: string;
}

export interface CreativeAsset {
  id: number;
  organization_id: number;
  gcs_uri: string;
  // Short-lived signed URL minted per response — never cache past the response that delivered it.
  signed_url: string;
  mime_type: string;
  byte_size: number;
  source: CreativeAssetSource;
  platform: AdPlatform | null;
  original_filename?: string | null;
  width_px?: number | null;
  height_px?: number | null;
  created_at: string;
  updated_at: string;
  tags: CreativeAssetTag[];
}

export interface CreativeAssetListResponse {
  items: CreativeAsset[];
  total: number;
  limit: number;
  offset: number;
}

export interface CreativeAssetListParams {
  limit?: number;
  offset?: number;
  platform?: AdPlatform;
  source?: CreativeAssetSource;
  /** Case-insensitive match on filename or tag value, applied server-side (#5383). */
  search?: string;
  /** Keep assets carrying ANY of these tag values, applied server-side (#5383). */
  tags?: string[];
}

/** Request for POST /assets/generate — AI image generation into the library (#4332). */
export interface CreativeAssetGenerateParams {
  prompt: string;
  /** square (Feed) or story (Stories/Reels). Backend default: square. */
  aspect?: 'square' | 'story';
  /** 1-4 images, one model request each. Backend default: 1. */
  n?: number;
  /** Platform the generations are attributed to. Backend default: META. */
  platform?: AdPlatform;
}

// One ZIP member skipped during upload, with a user-facing reason (#4784).
export interface SkippedZipEntry {
  filename: string;
  reason: string;
}

export interface CreativeAssetZipUploadResponse {
  items: CreativeAsset[];
  skipped: SkippedZipEntry[];
}

export interface MetaAdSetResponse {
  id: number;
  name: string;
  meta_ad_set_id: string;
  created_at: string;
  status: MetaEntityStatus;
  ads: MetaAdResponse[];
  billing_event?: string | null;
  optimization_goal?: string | null;
  daily_budget_cents?: number | null;
  // The raw Meta targeting spec (geo_locations etc.) — the Targeting tab renders it (#5981).
  targeting?: Record<string, unknown>;
}

export interface MetaCampaignResponse {
  id: number;
  // Opaque URL token the frontend routes on (/campaigns/meta/<slug>), mirroring Google's slug (#4659).
  slug: string;
  name: string;
  is_cbo: boolean;
  objective: string;
  offer_url: string;
  created_at: string;
  // NULL while the row is a draft the creation task hasn't pushed to Meta yet (#5981).
  meta_campaign_id: string | null;
  status: MetaEntityStatus;
  campaign_type: MetaCampaignType;
  daily_budget_cents?: number | null;
  // Spend-per-day regardless of budget mode (#5981): CBO = campaign budget, ABO = sum of its
  // ad sets' budgets (daily_budget_cents is null for ABO). Drives the sidebar's €X/d chip.
  effective_daily_budget_cents?: number | null;
  // Null = never launched (#5981): the sidebar shows 'ready to go live' instead of 'paused'.
  first_activated_at?: string | null;
}

export interface MetaCampaignDetailResponse extends MetaCampaignResponse {
  ad_sets: MetaAdSetResponse[];
  // The picked location selection as catalog references (#5981) — the Targeting tab resolves
  // names/reach from the shared Location cache and seeds its pickers with these. Null on
  // campaigns created before the columns existed.
  target_location_criteria_ids?: number[] | null;
  excluded_location_criteria_ids?: number[] | null;
  target_radii?: CampaignProximityInput[] | null;
  target_locales?: MetaLocaleEntry[] | null;
  // The Audience sub-tab's persisted demographic selection (#6076) — null = Meta defaults.
  target_audience?: MetaAudienceSelection | null;
  // The Interests sub-tab's persisted picks (#6076) — null/empty = broad targeting (Meta's default).
  target_interests?: MetaInterestEntry[] | null;
  // The Placements sub-tab's persisted selection (#6076) — null = Advantage+ automatic placements.
  target_placements?: MetaPlacementSelection | null;
  // Conversion settings (#6076): null optimization_event = the objective's default standard event;
  // null attribution_spec = Meta's default windows (7-day click + 1-day view).
  optimization_event?: MetaOptimizationEvent | null;
  attribution_spec?: MetaAttributionEntry[] | null;
  // The spend_cap circuit breaker (#6076) — one month's Meta allocation as a lifetime cap on Meta.
  spend_cap_cents?: number | null;
  // Bidding (#5981): null bid_strategy = Meta's default (LOWEST_COST_WITHOUT_CAP);
  // target_cpl_cents is the optimization detectors' threshold, edited on the Bidding panel.
  bid_strategy?: MetaBidStrategy | null;
  bid_amount_cents?: number | null;
  target_cpl_cents?: number | null;
}

/** `GET /meta/campaigns/{slug}/budget-recommendation` (#5981) — Meta learning-phase based figure. */
export interface MetaBudgetRecommendationResponse {
  daily_budget_cents: number;
  basis: string;
  cpl_cents: number;
}

/** The Meta bid strategies our lead campaigns can run (#5981) — Meta's own enum strings. */
export type MetaBidStrategy = 'LOWEST_COST_WITHOUT_CAP' | 'COST_CAP';

/**
 * `PATCH /meta/campaigns/{slug}/bidding` body (#5981). A provided bid_strategy pushes to Meta
 * (COST_CAP requires bid_amount_cents); target_cpl_cents is local-only (explicit null clears it)
 * and may be the sole field.
 */
export interface MetaCampaignBiddingUpdateRequest {
  bid_strategy?: MetaBidStrategy;
  bid_amount_cents?: number;
  target_cpl_cents?: number | null;
}

// Phase 7 (#3824) dashboards + Phase 5 card metrics. All snapshot-backed reads.
// `ctr` / `frequency` arrive as strings (backend Decimal serialises to a string) — Number() at render.

// An ad's latest detector verdict (Chunk 6b); null = no current verdict.
export type MetaPerformanceStatus = 'WINNER' | 'LOSER' | 'ON_THE_FENCE';

// The reporting window the dashboard figures cover (Chunk 6c). `attribution` is "account_default"
// since no attribution window is pinned on the Insights pull.
export interface MetaInsightsWindow {
  date_start: string;
  date_stop: string;
  attribution: string;
}

export interface MetaCampaignOverviewRow {
  meta_campaign_id: string;
  name?: string | null;
  campaign_type?: MetaCampaignType | null;
  status?: MetaEntityStatus | null;
  impressions: number;
  clicks: number;
  spend_cents: number;
  leads: number;
  cpl_cents?: number | null;
  frequency?: string | null;
  ctr?: string | null;
  winner_ad_count: number;
  loser_ad_count: number;
}

export interface MetaCampaignOverviewResponse {
  window: MetaInsightsWindow | null;
  rows: MetaCampaignOverviewRow[];
}

// Per-day Meta rollups for the consolidated Performance chart (CP-4/CP-6, #5971).
export interface MetaTimeSeriesRow {
  day: string;
  spend_cents: number;
  leads: number;
  clicks: number;
  impressions: number;
  cpl_cents?: number | null;
  ctr_pct?: number | null;
}

export interface MetaTimeSeriesResponse {
  window: MetaInsightsWindow | null;
  rows: MetaTimeSeriesRow[];
}

export interface MetaCreativePerformanceRow {
  meta_ad_id: string;
  name?: string | null;
  headline?: string | null;
  creative_asset_id?: number | null;
  impressions: number;
  clicks: number;
  spend_cents: number;
  leads: number;
  cpl_cents?: number | null;
  // Purchases + cost-per-purchase, extracted from raw_data actions (#5971 CP-5) — unified perf surface.
  purchases: number;
  cost_per_purchase_cents?: number | null;
  frequency?: string | null;
  ctr?: string | null;
  performance_status?: MetaPerformanceStatus | null;
}

export interface MetaCreativePerformanceResponse {
  window: MetaInsightsWindow | null;
  rows: MetaCreativePerformanceRow[];
}

export interface MetaAdSetPerformanceRow {
  meta_ad_set_id: string;
  name?: string | null;
  campaign_type?: MetaCampaignType | null;
  impressions: number;
  clicks: number;
  spend_cents: number;
  leads: number;
  cpl_cents?: number | null;
  frequency?: string | null;
  ctr?: string | null;
}

export interface MetaAdSetPerformanceResponse {
  window: MetaInsightsWindow | null;
  rows: MetaAdSetPerformanceRow[];
}

export interface MetaBudgetPacingRow {
  entity_level: 'CAMPAIGN' | 'ADSET';
  meta_id: string;
  name?: string | null;
  campaign_type?: MetaCampaignType | null;
  daily_budget_cents: number;
  spend_cents: number;
  pacing_pct?: number | null;
}

export interface MetaBudgetPacingResponse {
  window: MetaInsightsWindow | null;
  rows: MetaBudgetPacingRow[];
}

// Per-placement (publisher_platform: facebook / instagram / audience_network / messenger) metrics — a
// live account-level breakdown (#6049).
export interface MetaPlacementRow {
  value: string;
  leads: number;
  clicks: number;
  spend_cents: number;
  impressions: number;
  cpl_cents?: number | null;
  ctr_pct?: number | null;
}

export interface MetaPlacementResponse {
  rows: MetaPlacementRow[];
}

/** One HubSpot stage mapping uploading to Meta via CAPI — a Conversions-tab row (#5981). */
export interface MetaConversionMappingRow {
  stage_mapping_id: number;
  name: string;
  event_name: string;
  is_active: boolean;
  uploads_last_30d: number;
}

/** A HubSpot stage mapping not yet destined to Meta — an Add Conversion option (#5981). */
export interface MetaAvailableConversionRow {
  stage_mapping_id: number;
  name: string;
}

/** One event name the account's Pixel has measured (#5981) — a "Website · Pixel" row. */
export interface MetaPixelEventRow {
  event_name: string;
  count_last_7d: number;
}

export interface MetaConversionsResponse {
  linked: MetaConversionMappingRow[];
  available: MetaAvailableConversionRow[];
  // Meta-measured Pixel totals (browser + CAPI combined; Meta dedups) — a different metric from
  // the linked rows' upload counts, captioned as such in the UI.
  pixel_events: MetaPixelEventRow[];
}

/**
 * One ad the wizard wants to create (#4277 chunk 2b).
 *
 * One row = one Meta ad. Copy fields are optional since #5981: the wizard submits assets-only and
 * the backend creation task generates the copy in the background; it's edited inline on the
 * campaign screen afterwards. Provided fields are kept verbatim by the backend.
 */
export interface MetaAdCreativeInput {
  creative_asset_id: number;
  headline?: string;
  primary_text?: string;
  description?: string;
  /** CTA button rendered on the ad (#4655). The backend defaults to `LEARN_MORE` when omitted. */
  call_to_action?: MetaCallToAction;
}

/**
 * `POST /meta/campaigns` body (#4277 chunk 2b — per-phase).
 *
 * Replaces the pre-refactor `MetaThreeCampaignSetRequest`. The user creates Prospecting, HiPo, and
 * Retargeting campaigns independently — Prospecting first, HiPo once winners surface, Retargeting
 * later — each with that phase's budget and a fully-composed ad list. Backend enum values stay
 * `TESTING` / `SCALING` / `RETARGETING` (no DB rename in #4277 chunk 4); only the user-facing
 * labels are demo-aligned.
 *
 * Post-#4277 chunk 3: `page_id` is no longer in the request — the backend reads it from
 * `MetaAdAccount.page_id` (set via `POST /meta/ad-accounts/select-page`).
 */
/**
 * Meta campaign objectives (#6076) — ODAX values. No longer user-selectable since #6186: the
 * wizard picks a `MetaCampaignOutcome` and the backend derives the objective. This union keeps the
 * DISPLAY vocabulary (Overview field, result nouns); legacy pre-ODAX values on old campaigns read
 * as plain strings (`MetaCampaignResponse.objective: string`) and fall back safely in both maps.
 */
export type MetaObjective =
  | 'OUTCOME_AWARENESS'
  | 'OUTCOME_TRAFFIC'
  | 'OUTCOME_ENGAGEMENT'
  | 'OUTCOME_LEADS'
  | 'OUTCOME_SALES';

/** Display labels for objectives — the Overview field (#6076; the wizard picks outcomes since #6186). */
export const META_OBJECTIVE_LABEL: Record<string, string> = {
  OUTCOME_AWARENESS: 'Awareness',
  OUTCOME_TRAFFIC: 'Traffic',
  OUTCOME_ENGAGEMENT: 'Engagement',
  OUTCOME_LEADS: 'Leads',
  OUTCOME_SALES: 'Sales',
};

/**
 * What one delivery result is called per objective (#6076) — keeps the Bidding panel's copy honest
 * ("Get the most leads/purchases/landing page views from your budget").
 */
export const META_OBJECTIVE_RESULT_NOUN: Record<string, string> = {
  OUTCOME_AWARENESS: 'reach',
  OUTCOME_TRAFFIC: 'landing page views',
  OUTCOME_ENGAGEMENT: 'engagements',
  OUTCOME_LEADS: 'leads',
  OUTCOME_SALES: 'purchases',
};

/** What the campaign should get the client (#6186) — expands to the full Meta API stack server-side. */
export type MetaCampaignOutcome = 'WEBSITE_LEADS' | 'INSTANT_FORM_LEADS' | 'WEBSITE_SALES';

/** One PUBLISHED instant (lead-gen) form on the bound Facebook Page (#6186). */
export interface MetaLeadgenFormInfo {
  id: string;
  name?: string | null;
}

export interface MetaPhaseCampaignRequest {
  phase: MetaCampaignType;
  offer_url: string;
  daily_budget_cents: number;
  ads: MetaAdCreativeInput[];
  // What the campaign should get the client (#6186) — omitted = website leads (the server default).
  outcome?: MetaCampaignOutcome;
  // The published instant form for INSTANT_FORM_LEADS (#6186); meaningless for website outcomes.
  leadgen_form_id?: string | null;
  // Creation-time optimization-event pick (#6076) — website (Pixel-backed) outcomes only;
  // null/omitted = the outcome's default standard event. Meta makes conversion settings immutable
  // once the ad set publishes (subcode 3260011), so creation is the only place to choose.
  optimization_event?: MetaConversionEventOption | null;
  target_cpl_cents?: number | null;
  targeting?: Record<string, unknown> | null;
  // #5907: the wizard's location step — shared Location cache criteria ids + Radius-tab entries,
  // resolved to Meta geo targeting server-side. Absent = target the business's locations.
  target_location_criteria_ids?: number[] | null;
  target_radii?: CampaignProximityInput[] | null;
}

/**
 * `PATCH /budgets/{platform}/{entity}/{id}` body (#4288 Phase 1). Cross-platform — Google's
 * value is whole-currency floor-divided server-side, Meta's stays in cents end-to-end.
 */
export interface BudgetUpdateRequest {
  daily_budget_cents: number;
}

/** Response from `PATCH /budgets/meta/campaigns/{id}` — the persisted CBO campaign budget. */
export interface MetaCampaignBudgetResponse {
  id: number;
  meta_campaign_id: string;
  daily_budget_cents: number | null;
}

/** Response from `PATCH /budgets/meta/ad-sets/{id}` — the persisted ABO ad-set budget. */
export interface MetaAdSetBudgetResponse {
  id: number;
  meta_ad_set_id: string;
  daily_budget_cents: number | null;
}

/** `PATCH /meta/campaigns/{id}/status` body (#4288 Phase 1c). `ACTIVE` to launch, `PAUSED` to pause. */
export interface MetaCampaignStatusRequest {
  status: MetaEntityStatus;
}

/** Body for `PATCH /meta/campaigns/ads/{ad_id}/status` — per-ad pause/resume (#6179). */
export interface MetaAdStatusUpdateRequest {
  status: MetaEntityStatus;
}

/** Response from `PATCH /meta/campaigns/{id}/status` — the persisted campaign status after a launch/pause. */
export interface MetaCampaignStatusResponse {
  id: number;
  meta_campaign_id: string;
  status: MetaEntityStatus;
}

/**
 * Backend `TaskTrackingState` mirror — same string values the Python enum emits. The frontend uses
 * the discriminator to decide when to stop polling the Meta progress endpoint (#4288 Phase 4).
 */
export type TaskTrackingState = 'IN_PROGRESS' | 'COMPLETED' | 'FAILED';

/** One resource row in the Meta progress payload — running `completed` vs original `total`. */
export interface MetaResourceProgress {
  total: number;
  completed: number;
}

/**
 * Response from `GET /meta/campaigns/{id}/progress` (#4288 Phase 1d). Per-resource progress for an
 * in-flight Meta campaign generation. Mirrors `MetaCampaignGenerationProgress` in the backend.
 */
export interface MetaCampaignGenerationProgress {
  state: TaskTrackingState;
  ads: MetaResourceProgress;
  ad_sets: MetaResourceProgress;
  error_message?: string | null;
}

/** One Facebook Page the user admins (#4277 chunk 3), as returned by `GET /meta/ad-accounts/{id}/pages`. */
export interface MetaPageInfo {
  id: string;
  name: string;
  category?: string | null;
}

/** Body for `POST /meta/ad-accounts/select-page` (#4277 chunk 3) — bind a Page to a MetaAdAccount. */
export interface MetaPageSelectRequest {
  meta_ad_account_id: number;
  page_id: string;
  page_name?: string | null;
}

/** One Meta Pixel on an ad account (Phase 7 Chunk 0), as returned by `GET /meta/ad-accounts/{id}/pixels`. */
export interface MetaPixelInfo {
  id: string;
  name?: string | null;
}

/** Body for `POST /meta/ad-accounts/select-pixel` (Phase 7 Chunk 0) — bind a Pixel to a MetaAdAccount. */
export interface MetaPixelSelectRequest {
  meta_ad_account_id: number;
  pixel_id: string;
  pixel_name?: string | null;
}

export interface MetaCampaignDispatchResponse {
  offer_url: string;
  task_tracking_id: number;
  // The campaign row is created synchronously (#5981) — navigate straight to /campaigns/meta/{slug}.
  slug: string;
  status: string;
}

export interface MetaCampaignTaskStatusResponse {
  state: 'IN_PROGRESS' | 'COMPLETED' | 'FAILED';
  error_message?: string | null;
  // IN_PROGRESS with no task update past the shared 75-minute threshold (#5981) — drives the
  // "Campaign Stuck Building" banner, mirroring Google's stuck_in_progress.
  is_stuck?: boolean;
}

/** Ad platforms a stage's conversions are sent to. Only GOOGLE_ADS is wired up today; META lands with Phase 5. */
export type ConversionPlatform = 'GOOGLE_ADS' | 'META';

export interface StageConfigRequest {
  stage_id: string;
  stage_label: string;
  stage_order: number;
  is_conversion_event: boolean;
  funnel_stage?: string;
  conversion_value?: number;
  conversion_action_name?: string;
  primary_for_goal?: boolean;
  /** Ad platforms this stage's conversions fire to. Omitted/empty defaults to ["GOOGLE_ADS"] server-side. */
  platforms?: ConversionPlatform[];
}

export interface PipelineConfigCreateRequest {
  pipeline_id: string;
  pipeline_label: string;
  stages: StageConfigRequest[];
}

export interface StageDestination {
  id: number;
  is_active: boolean;
  platform: ConversionPlatform;
}

export interface StageMappingResponse {
  id: number;
  stage_id: string;
  stage_label?: string;
  stage_order?: number;
  is_conversion_event: boolean;
  funnel_stage?: string;
  conversion_value?: number;
  conversion_action_name?: string;
  primary_for_goal?: boolean;
  destinations?: StageDestination[];
  google_ads_conversion_action_id?: string;
  google_ads_conversion_action_name?: string;
  google_ads_account_id?: string;
}

export interface PipelineConfigResponse {
  id: number;
  pipeline_id: string;
  pipeline_label?: string;
  is_active: boolean;
  organization_id: number;
  integration_id: number;
  created_at: string;
  stages: StageMappingResponse[];
}

// Keyword Metrics Backfill Types
export interface BackfillTaskResponse {
  task_id: string;
  campaign_id: number;
  status: string;
  total_keywords: number;
  keywords_needing_metrics: number;
  message: string;
}

// Account Deletion Types
export interface AccountDeletionResponse {
  success: boolean;
  message: string;
  campaigns_paused: number;
  organizations_affected: number[];
}

// User Notification Settings Types
export interface UserNotificationSettings {
  email_notifications_enabled: boolean;
}

export interface UserNotificationSettingsUpdate {
  email_notifications_enabled?: boolean;
}

export interface CampaignSidebarFilters {
  types?: string[];
  statuses?: string[];
  platforms?: string[];
}

export interface NotificationFiltersSettings {
  sources?: string[];
  warning_levels?: string[];
}

export interface TaskFiltersSettings {
  action_type?: string;
  campaign_id?: string;
}

export interface OptimizationFiltersSettings {
  types?: string[];
  campaign_ids?: string[];
  statuses?: string[];
}

export interface FiltersSettings {
  campaign_sidebar?: CampaignSidebarFilters;
  notifications?: NotificationFiltersSettings;
  tasks?: TaskFiltersSettings;
  optimizations?: OptimizationFiltersSettings;
}

export interface WidgetLayoutItem {
  id: string;
  width: 33 | 50 | 66 | 100;
  order: number;
}

export interface SavedView {
  /** User-chosen label shown in the Views dropdown. */
  name: string;
  /** Grouping selection within the tab (e.g. "by-type", "by-campaign", "by-ad-group"). */
  group_by: string;
  /** Column ids the user wants visible when this view is active. */
  visible_columns: string[];
}

export interface PerformancePreferences {
  /** Comparison-period selection persisted from the perf-v2 header (#4293). */
  compare_mode?: string;
  /** Per-tab map of visible column ids for the column-picker (#4294). Tab key
   * → list of column ids the user has chosen to see. Absent → table renders
   * with its default visibility set. */
  column_visibility?: Record<string, string[]>;
  /** Per-tab list of named SavedView configurations (#4294). */
  saved_views?: Record<string, SavedView[]>;
  /** Per-table rows-per-page, remembered between visits (#6758). Table key →
   * the size the reader chose. Absent → the table's own default. */
  rows_per_page?: Record<string, number>;
}

export interface UserSettings {
  filters?: FiltersSettings;
  widget_layouts?: Record<string, WidgetLayoutItem[]>;
  performance?: PerformancePreferences;
}

export type UserSettingsUpdate = UserSettings;

// Optimisation Types
export interface OptimisationConfig {
  optimisation_enabled: boolean;
  optimisation_config: {
    skag_conversion_threshold: number;
    location_cpa_threshold_percent: number;
    max_campaigns_per_account: number;
    require_approval_first_change: boolean;
  };
}

export interface OptimisationConfigUpdate {
  optimisation_enabled?: boolean;
  optimisation_config?: Partial<OptimisationConfig['optimisation_config']>;
}

export interface OptimisationHistoryResponse {
  items: ActionLog[];
  total: number;
}

export interface OptimisationOpportunity {
  id: string;
  type: 'SKAG_PROMOTION' | 'LOCATION_CAMPAIGN' | 'ICP_CAMPAIGN';
  title: string;
  description: string;
  potential_impact: string;
  created_at: string;
  data: Record<string, unknown>;
}

export type HeadlinePerformanceLabel = 'BEST' | 'GOOD' | 'LOW' | 'LEARNING' | 'PENDING';

export interface HeadlineWithMetrics {
  headline_id: number;
  headline_text: string;
  ad_id: number;
  funnel_stage: string | null;
  impressions: number;
  clicks: number;
  ctr: number;
  conversions: number;
  cost_micros: number;
  cost_dollars: number;
  performance_label: HeadlinePerformanceLabel | null;
}

export interface CampaignHeadlineMetricsResponse {
  campaign_id: number;
  /** Null (#6281): headline asset totals are lifetime — the report supports no date window. */
  date_range_days: number | null;
  headlines: HeadlineWithMetrics[];
  last_fetched_at: string | null;
  fetched_from_cache: boolean;
}

export interface DescriptionWithMetrics {
  description_id: number;
  description_text: string;
  ad_id: number;
  funnel_stage: string | null;
  impressions: number;
  clicks: number;
  ctr: number;
  conversions: number;
  cost_micros: number;
  cost_dollars: number;
  performance_label: HeadlinePerformanceLabel | null;
}

export interface CampaignDescriptionMetricsResponse {
  campaign_id: number;
  date_range_days: number;
  descriptions: DescriptionWithMetrics[];
  last_fetched_at: string | null;
  fetched_from_cache: boolean;
}

// External Google Ads Campaign Types (read-only)
export interface ExternalCampaignSummary {
  google_ads_campaign_id: string;
  name: string;
  campaign_type: string;
  status: string;
  budget_micros: number | null;
  impressions: number;
  clicks: number;
  cost_micros: number;
  conversions: number;
  last_fetched_at: string;
}

export interface ExternalKeyword {
  keyword_id: string;
  text: string;
  match_type: string;
  impressions: number;
  clicks: number;
  cost_micros: number;
  conversions: number;
}

export interface ExternalAd {
  ad_id: string;
  ad_type: string;
  headlines: string[];
  descriptions: string[];
  final_urls: string[];
}

export interface ExternalAdGroup {
  ad_group_id: string;
  name: string;
  status: string;
  ads: ExternalAd[];
  keywords: ExternalKeyword[];
}

export interface ExternalExtensions {
  sitelinks: Record<string, unknown>[];
  callouts: string[];
  structured_snippets: Record<string, unknown>[];
}

export interface ExternalCampaignDetail {
  google_ads_campaign_id: string;
  name: string;
  campaign_type: string;
  status: string;
  budget_micros: number | null;
  bidding_strategy_type: string | null;
  start_date: string | null;
  end_date: string | null;
  impressions: number;
  clicks: number;
  cost_micros: number;
  conversions: number;
  ad_groups: ExternalAdGroup[];
  extensions: ExternalExtensions | null;
  last_fetched_at: string;
}

// ---- BusinessLocation ----------------------------------------------------

export type BusinessLocationTier = 'global' | 'national' | 'regional';
export type BusinessLocationVariant = 'global' | 'country' | 'admin' | 'radius';
export type BusinessLocationSource = 'user' | 'agent' | 'backfill';

export interface BusinessLocationRead {
  uuid: string;
  tier: BusinessLocationTier;
  variant: BusinessLocationVariant;
  country_code: string | null;
  location_id: number | null;
  latitude: number | null;
  longitude: number | null;
  radius_km: number | null;
  radius_address: string | null;
  /** #5497: unit the radius was entered in; display-only, radius_km stays canonical. null = km. */
  radius_unit: 'km' | 'mi' | null;
  source: BusinessLocationSource;
  dismissed_by_user: boolean;
  created_at: string;
  updated_at: string;
  is_resolvable: boolean;
  /** #4816: campaigns of this business that target this location. Non-empty => row is locked from removal. */
  linked_campaigns: LinkedCampaign[];
}

export interface LinkedCampaign {
  name: string;
  slug: string;
  /** #6076: which detail route to link to. Meta campaigns share the same Location criteria ids as Google. */
  platform: 'google' | 'meta';
  /** #6178: meta campaign_type, so the pill can apply metaCampaignDisplayName like the campaign pages. */
  campaign_type?: string | null;
}

export interface GeocodeResultRead {
  place_id: string;
  formatted_address: string;
  lat: number;
  lng: number;
  country_code: string | null;
  place_type: string | null;
}

export interface BusinessLocationCreate {
  tier: BusinessLocationTier;
  variant: BusinessLocationVariant;
  country_code?: string | null;
  location_id?: number | null;
  latitude?: number | null;
  longitude?: number | null;
  radius_km?: number | null;
  radius_address?: string | null;
  radius_unit?: 'km' | 'mi' | null;
  // Selection-data passthrough for catalog (country/admin) adds: lets the backend
  // materialize an uncached geo-target into the Location cache instead of FK-failing
  // (#4733 parity). Not persisted on the BusinessLocation.
  name?: string | null;
  canonical_name?: string | null;
  target_type?: string | null;
  parent_id?: number | null;
  status?: string | null;
}

export interface BusinessLocationUpdate {
  country_code?: string | null;
  location_id?: number | null;
  latitude?: number | null;
  longitude?: number | null;
  radius_km?: number | null;
  radius_address?: string | null;
}

/** One entry in a bulk reconcile (PUT /business/{id}/locations). Same shape as a
 * create plus an optional `uuid`: present for rows already on the server (so the
 * server matches by identity and applies radius edits), absent for new adds. */
export interface BusinessLocationReconcileItem extends BusinessLocationCreate {
  uuid?: string | null;
}

export interface LocationContextRead {
  uuid: string;
  context: Record<string, unknown>; // typed shape kept loose in v1; LocationContextPayload server-side
  source: 'agent' | 'backfill';
  version: number;
  detected_at: string;
  created_at: string;
  updated_at: string;
}

export interface LocationTaskTriggerResponse {
  task_id: number;
  business_id: number;
}

export type LocationTaskState = 'IN_PROGRESS' | 'COMPLETED' | 'FAILED';

export interface LocationTaskStatusRead {
  state: LocationTaskState;
  error_message: string | null;
}

// ─── Optimisation campaign creation (HiPo / ICP / Location) ──────────────────
// Mirrors backend/app/campaign/schemas.py for the unified
// POST /organizations/{org_id}/campaigns/optimisation endpoint.

export type OptimisationCampaignType = 'icp' | 'hipo' | 'location';

// Type-specific payload shapes. Each is a passthrough dict the backend
// forwards to OptimisationCampaignCreateService via the Celery task.

export type IcpDataPayload = {
  icp_id: string;        // UUID from business.icps
  icp_name: string;
  icp_description?: string;
  keywords?: string[];
  pains?: string[];
  goals?: string[];
};

export type IntentDataPayload = {
  // KeywordIntent picker id. Nullable for the user-driven HiPo wizard path
  // (no intent picker in the new 2-step wizard) — backend's
  // `_create_hipo_keywords` accepts `intent_id is None`.
  intent_id: number | null;
  intent_name: string;
  keywords: string[];
};

export type LocationDataPayload = {
  location_id: number;   // criteria_id of the picked geo target
  location_name: string;
  target_type?: string;
};

// Mirrors backend LocationInput (extra="forbid"): the API only accepts
// criteria_id + optional name/target_type. The full FE Location type carries
// extra picker-only fields (country_code, parent_id, reach, status,
// canonical_name) that the backend rejects, so wizards must slim down.
export type LocationInputPayload = {
  criteria_id: number;
  name?: string;
  target_type?: string;
};

export type OptimisationCampaignCreatePayload = {
  campaign_type: OptimisationCampaignType;
  // Optional for the HiPo manual-keywords wizard tab. ICP/Location still
  // require a source; when omitted the backend inherits googleadsaccount /
  // target_locations / website_url from the Business referenced by
  // business_id.
  source_campaign_id?: number;
  business_id: number;          // must match source_campaign.business_id when source is set
  // Optional top-level landing-page override. When set, the backend uses it as the
  // campaign website_url instead of inheriting from the source campaign / Business;
  // wizards prefill it from the source campaign's URL.
  website_url?: string;
  target_locations: LocationInputPayload[];  // empty array → backend falls back to source campaign's or Business's
  budget_amount?: number;        // 0 → use Google's recommendation post-create
  icp_data?: IcpDataPayload;
  intent_data?: IntentDataPayload;
  location_data?: LocationDataPayload;
};

// 202 response from the unified endpoint. The endpoint pre-creates a stub
// Campaign row (DRAFT, "Generating…" name) synchronously so the wizard can
// navigate straight to /campaigns/$slug; the Celery task fills in ads,
// keywords, and other associations on top of that row.
export type OptimisationCampaignCreateAccepted = {
  message: string;
  source_campaign_id: number | null;
  campaign_type: OptimisationCampaignType;
  campaign_id: number;
  slug: string;
  task_tracking_id: number | null;
};

// HiPo wizard step 2 — keyword multi-select row. Mirrors backend
// `CampaignKeywordWithPerf`. Performance fields default to 0 when no live
// GAds data exists yet (e.g. DRAFT source campaigns not yet published).
// conversion_share = keyword conversions / total ad_group conversions.
// is_hipo_recommended = conversion_share >= 0.5 AND conversions >= 5 AND clicks >= 50.
export type CampaignKeywordWithPerf = {
  id: number;
  text: string;
  match_type: string;
  is_paused: boolean;
  ad_id: number;
  impressions: number;
  clicks: number;
  conversions: number;
  cost_micros: number; // Google Ads stores cost in micros (1 currency unit = 1_000_000)
  conversion_share: number;
  is_hipo_recommended: boolean;
};

// AI landing pages (#5701). Mirrors backend LandingPageResponse.
export type LandingPageStatus = 'GENERATING' | 'PREVIEW' | 'PUBLISHED' | 'ERROR';

export interface LandingPage {
  public_id: string;
  status: LandingPageStatus;
  preview_url: string | null;
  error_detail: string | null;
  model_used: string | null;
  leads_count: number;
  sections: string[]; // section names, for vibe-edit targeting
  edit_in_progress: boolean;
  last_edit_error: string | null;
}

export interface LandingPageLead {
  id: number;
  data: Record<string, string>;
  gclid: string | null;
  ip_address: string | null;
  user_agent: string | null;
  created_at: string;
}

// One turn of a landing page's vibe-edit conversation. Mirrors backend LandingPageEditMessageResponse.
export interface LandingPageEditMessage {
  id: number;
  role: 'user' | 'assistant';
  body: string;
  section: string | null; // assistant-only: the section the edit resolved to
  status: 'applied' | 'failed' | null; // assistant-only
  created_at: string;
}

// Public API keys (#6234)
export enum ApiKeyEnvironment {
  LIVE = 'LIVE',
  TEST = 'TEST',
}

export interface ApiKeyData {
  id: string;
  name: string;
  environment: ApiKeyEnvironment;
  prefix: string;
  last_four: string;
  scopes: string[];
  is_active: boolean;
  expires_at: string;
  last_used_at: string | null;
  created_at: string;
  is_own: boolean;
}

export interface ApiKeyWithSecret extends ApiKeyData {
  secret: string;
}

export interface ApiKeyListData {
  keys: ApiKeyData[];
}

/** An app connected through OAuth, such as Claude, that can still act for the signed-in person (#6612). */
export interface OauthConnection {
  id: string;
  client_name: string;
  scopes: string[];
  connected_at: string;
  last_used_at: string;
  surfaces: ('mcp' | 'api')[];
}

export interface OauthConnectionList {
  connections: OauthConnection[];
}

// OAuth consent screen (#6239, D11)
export interface OauthConsentClient {
  client_id: string;
  client_name: string;
  logo_uri: string | null;
  client_uri: string | null;
}

export interface OauthConsentOrganization {
  id: number;
  name: string;
  slug: string;
  role: string;
  allowed: boolean;
}

export interface OauthConsentExistingGrant {
  scopes: string[];
  // Null for all-organization grants; a number marks a legacy single-org grant.
  organization_id: number | null;
}

export interface OauthConsentContext {
  client: OauthConsentClient;
  scopes: string[];
  resource: string;
  redirect_uri: string;
  code_challenge: string;
  code_challenge_method: string;
  state: string | null;
  organizations: OauthConsentOrganization[];
  existing_grant: OauthConsentExistingGrant | null;
}

export interface OauthConsentParams {
  client_id: string;
  redirect_uri: string;
  scope: string;
  resource: string;
  code_challenge: string;
  state?: string;
}

export interface OauthConsentApproveBody extends OauthConsentParams {
  // The scopes the user left checked; the grant covers all their organizations.
  approved_scopes: string[];
}

export interface OauthConsentRedirect {
  redirect_to: string;
}


/** One named import-eligibility check (#6381 chunk B/E). */
export interface ImportCheck {
  id: string;
  passed: boolean;
  detail: string;
}

export interface ImportChecksResult {
  eligible: boolean;
  checks: ImportCheck[];
}
